import type { Dayjs } from 'dayjs'
import React, { createContext, useContext, useMemo } from 'react'

import type { DateInput, TimezoneAbbreviation } from '@fourthwall/utils/dist/timezone'
import {
  convertLocalDateToTimezonedTimestamp,
  convertLocalToUTC,
  convertTimezonedTimestampToLocalDate,
  convertToLocalDate,
  formatDateInTimezone,
  formatUTCWithTimezoneOffset,
  parseUTCToLocal,
} from '@fourthwall/utils/dist/timezone'

import { useGetShopTimezone } from '../hooks/useGetShopTimezone'

type ShopTimeContextType = {
  timezone: string
  timezoneAbbreviation: TimezoneAbbreviation
  getCurrentShopTime: () => Dayjs
  getCurrentRelativeShopTime: () => Date
  toShopTime: (date: DateInput) => ReturnType<typeof parseUTCToLocal>
  toUTC: (date: DateInput) => ReturnType<typeof convertLocalToUTC>
  formatInShopTime: (date: DateInput, formatStr?: string) => ReturnType<typeof formatDateInTimezone>
  toUTCWithOffset: (date: DateInput, formatStr?: string) => string
  convertDateToTimestamp: (date: Date) => ReturnType<typeof convertLocalDateToTimezonedTimestamp>
  convertTimestampToDate: (
    timestamp: number,
  ) => ReturnType<typeof convertTimezonedTimestampToLocalDate>
  convertToDate: (date: DateInput) => ReturnType<typeof convertToLocalDate>
}

const defaultTimezone = 'UTC'
const defaultTimezoneAbbreviation = 'CET'

const ShopTimeContext = createContext<ShopTimeContextType>({
  timezone: defaultTimezone,
  timezoneAbbreviation: defaultTimezoneAbbreviation,
  getCurrentShopTime: () => parseUTCToLocal(new Date(), defaultTimezone),
  getCurrentRelativeShopTime: () => new Date(),
  toShopTime: (date: DateInput) => parseUTCToLocal(date, defaultTimezone),
  toUTC: (date: DateInput) => convertLocalToUTC(date, defaultTimezone),
  formatInShopTime: (date: DateInput, formatStr?: string) =>
    formatDateInTimezone(date, defaultTimezone, formatStr),
  toUTCWithOffset: (date: DateInput, formatStr?: string) =>
    formatUTCWithTimezoneOffset(date, defaultTimezone, formatStr),
  convertDateToTimestamp: (date: Date) =>
    convertLocalDateToTimezonedTimestamp(date, defaultTimezone),
  convertTimestampToDate: (timestamp: number) =>
    convertTimezonedTimestampToLocalDate(timestamp, defaultTimezone),
  convertToDate: (date: DateInput) => convertToLocalDate(date, defaultTimezone),
})

export const ShopTimeProvider = ({ children }: { children: React.ReactNode }) => {
  const shopTimezone = useGetShopTimezone()
  const timezone = shopTimezone.name

  const toShopTime = useMemo(() => (date: DateInput) => parseUTCToLocal(date, timezone), [timezone])

  const toUTC = useMemo(() => (date: DateInput) => convertLocalToUTC(date, timezone), [timezone])

  const formatInShopTime = useMemo(
    () =>
      (date: DateInput, formatStr = 'YYYY-MM-DD HH:mm') =>
        formatDateInTimezone(date, timezone, formatStr),
    [timezone],
  )

  const toUTCWithOffset = useMemo(
    () =>
      (date: DateInput, formatStr = 'YYYY-MM-DDTHH:mm:ssZ') =>
        formatUTCWithTimezoneOffset(date, timezone, formatStr),
    [timezone],
  )

  const getCurrentShopTime = useMemo(() => {
    return () => toShopTime(new Date())
  }, [toShopTime])

  const getCurrentRelativeShopTime = useMemo(() => {
    return () => new Date(getCurrentShopTime().format('YYYY-MM-DDTHH:mm:ss'))
  }, [getCurrentShopTime])

  const convertDateToTimestamp = useMemo(
    () => (date: Date) => convertLocalDateToTimezonedTimestamp(date, timezone),
    [timezone],
  )

  const convertTimestampToDate = useMemo(
    () => (timestamp: number) => convertTimezonedTimestampToLocalDate(timestamp, timezone),
    [timezone],
  )

  const convertToDate = useMemo(
    () => (date: DateInput) => convertToLocalDate(date, timezone),
    [timezone],
  )

  const value = useMemo(
    () => ({
      timezone,
      timezoneAbbreviation: shopTimezone.abbreviation,
      getCurrentShopTime,
      getCurrentRelativeShopTime,
      toShopTime,
      toUTC,
      formatInShopTime,
      toUTCWithOffset,
      convertDateToTimestamp,
      convertTimestampToDate,
      convertToDate,
    }),
    [
      formatInShopTime,
      shopTimezone.abbreviation,
      timezone,
      getCurrentShopTime,
      getCurrentRelativeShopTime,
      toShopTime,
      toUTC,
      toUTCWithOffset,
      convertDateToTimestamp,
      convertTimestampToDate,
      convertToDate,
    ],
  )

  return <ShopTimeContext.Provider value={value}>{children}</ShopTimeContext.Provider>
}

export const useShopTime = () => useContext(ShopTimeContext)
